import './parties-ranking-table.scss';

import PropTypes from 'prop-types';
import React from 'react';

import t from '../../../labels/labelling-utils';
import { TableHead } from '../../atoms';
import Table, { IColumn, IRow } from '../../molecules/table/table';
import { IClassNames, makeClassNames } from '../../../utils/styles';
import { numberFormat } from '../../../utils/numbers';
import { changeTextCase } from '../../../utils/string-utils';

export interface IRowExtended extends IRow {
  isCandidate: boolean;
  isElected?: boolean | null;
  name: string;
  votes?: number | null;
  percentage?: number | null;
  seats?: number | null;
  isBorderless?: boolean;
}

/**
 * IPartiesRankingTable interface
 * @param className - The name used to create dynamic CSS class names
 * @param pagination - An OBJECT returned from API to manage PAGINATION (still TBD)
 * @param rows - ARRAY of OBJECTS data rows
 * @param showTitles - Boolean to show/hide THEAD
 * @param party - Array of PARTY NAMES to be SELECTED
 * @param partyId - PARTY ID to be SELECTED (for new PARTY RANKINGS template)
 */
export interface IPartiesRankingTable {
  className?: string;
  pagination?: object;
  rows: Array<Array<IRowExtended>>;
  showTitles?: boolean;
  partiesSelected?: Array<string>;
  partyId?: string;
  trackingData?: object;
  isEuropean?: boolean;
}

const createColumn = (titleKey: string, dataKey: string, classModifier: string) => ({
  title: t(titleKey),
  data: dataKey,
  classModifier,
  render: (value: number | null) => {
    if (!value) {
      return '-';
    }
    return typeof value === 'number' ? numberFormat(value) : value;
  },
});
const getColumns = (hasSeats: boolean): Array<IColumn> => {
  const columns = [
    createColumn('parties.ranking.th.name', 'name', 'name'),
    createColumn('parties.ranking.th.votes', 'votes', 'votes'),
    createColumn('parties.ranking.th.percentage', 'percentage', 'percentage'),
  ];

  if (hasSeats) {
    columns.push(createColumn('parties.ranking.th.seats', 'seats', 'seats'));
  }

  return columns;
};
const classModifierNames = (className: string, classModifier: string): IClassNames => {
  return makeClassNames({
    columnClassModifier: `${className}__head-cell--${classModifier} text--font-size-12 text--weight-500`,
  });
};

const classNames = (className: string, isEuropean: boolean): IClassNames => {
  return makeClassNames({
    table: className,
    thead: `${className}__head`,
    tr: `${className}__head-row${isEuropean ? '--european' : ''}`,
    candidateColumnModifier: `${className}-container--is-candidate`,
    candidateElectedColumnModifier: `${className}-container--is-elected`,
  });
};

const getColumnTitleKeys = (columns: Array<IColumn>, isEuropean: boolean) => {
  return columns.map((column) => {
    if (column.data === 'name' && isEuropean) {
      return `parties.ranking.th.name.european`;
    } else {
      return `parties.ranking.th.${column.data}`;
    }
  });
};

const getColumnClassName = (className: string, columns: Array<IColumn>) => {
  return columns.map((column) => {
    const cn = column.classModifier && classModifierNames(className, column.classModifier);
    return cn !== '' && cn?.columnClassModifier;
  });
};

/**
 * Renders a TABLE component passing an ARRAY of ROWS
 * @param className - The name used to create dynamic CSS class names
 * @param pagination - An OBJECT returned from API to manage PAGINATION (still TBD)
 * @param rows - ARRAY of arrays containing RowExtended ROWS
 * @param showTitles - Boolean to show/hide THEAD
 * @param isEuropean - Boolean for european variant
 */
const PartiesRankingTable: React.FC<IPartiesRankingTable> = ({
  /* istanbul ignore next: default value */
  className = 'parties-ranking',
  /* istanbul ignore next: default value */
  pagination = {},
  /* istanbul ignore next: default value */
  rows = [],
  /* istanbul ignore next: default value */
  showTitles = true,
  isEuropean = false,
}) => {
  const hasSeats = rows?.flat().some((candidate) => candidate.seats && candidate.seats > 0);
  const columns = getColumns(hasSeats);
  const columnTitles = getColumnTitleKeys(columns, isEuropean) as string[]; // Need to force this as a strings array to let it work in <TableHead /> component

  const columnClassNames = getColumnClassName(className, columns) as string[]; // Need to force this as a strings array to let it work in <TableHead /> component
  const getIsCandidateClass = (row: Array<IRowExtended>) => (row[0].isCandidate ? cn.candidateColumnModifier : '');
  const getIsElectedClass = (row: Array<IRowExtended>) => (row[0].isElected ? cn.candidateElectedColumnModifier : '');

  const cn = classNames(className, isEuropean);

  const rowsTitleCased = rows.map((row) =>
    row.map((rowExtended) => {
      return {
        ...rowExtended,
        name:
          rowExtended.isCandidate || isEuropean
            ? changeTextCase(rowExtended.name, 'UPPERCASE')
            : changeTextCase(rowExtended.name, 'TITLECASE'),
      };
    })
  );

  return (
    <React.Fragment>
      <table className={cn.table}>
        <TableHead
          columns={columnTitles}
          isVisible
          thClassNames={columnClassNames}
          theadClassName={cn.thead}
          trClassName={cn.tr}
        />
      </table>
      {rowsTitleCased.map((row, index) => {
        const rowsLenght = rowsTitleCased.length - 1;
        if (isEuropean && index === rowsLenght && row.length - 1 >= 0) {
          row[row.length - 1].isBorderless = true;
        }
        return (
          <div className={`${getIsCandidateClass(row)} ${getIsElectedClass(row)}`} key={index}>
            <Table
              columns={columns}
              rows={row}
              className={isEuropean ? `${className}--european` : className}
              showTitles={showTitles}
            />
          </div>
        );
      })}
    </React.Fragment>
  );
};

/* istanbul ignore else */
if (process.env.NODE_ENV !== 'production') {
  PartiesRankingTable.propTypes = {
    className: PropTypes.string,
    isEuropean: PropTypes.bool,
    pagination: PropTypes.object,
    rows: PropTypes.arrayOf(
      PropTypes.arrayOf(
        PropTypes.shape({
          isCandidate: PropTypes.bool.isRequired,
          isElected: PropTypes.bool,
          name: PropTypes.string.isRequired,
          percentage: PropTypes.number,
          seats: PropTypes.number,
          votes: PropTypes.number,
        }).isRequired
      ).isRequired
    ).isRequired,
    showTitles: PropTypes.bool,
    trackingData: PropTypes.object,
  };
}

export default PartiesRankingTable;
